import type { BrainRepoWriter } from "./types.js";
import type { DreamDigest } from "./digest.js";
import { renderDigestMarkdown } from "./digest.js";

export interface DigestCommitResult {
  committed: boolean;
  newSha?: string;
  /** Set when the write/verify failed — the digest payload, handed back for re-emit / log (never lost). */
  reconstructible?: DreamDigest;
  reason?: "non-fast-forward" | "error" | "verify-failed";
}

const DIGEST_DIR = "artifacts/dream-digest";
const ISO_UTC = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?Z$/;

function digestPaths(cycle: string): { json: string; md: string } {
  return { json: `${DIGEST_DIR}/${cycle}.json`, md: `${DIGEST_DIR}/${cycle}.md` };
}

function normalizeCycleTimestamp(cycle: string): string | null {
  const match = ISO_UTC.exec(cycle);
  if (!match) return null;
  const parsed = new Date(cycle);
  if (!Number.isFinite(parsed.getTime())) return null;
  const [, year, month, day, hour, minute, second] = match.map(Number);
  if (
    parsed.getUTCFullYear() !== year || parsed.getUTCMonth() + 1 !== month ||
    parsed.getUTCDate() !== day || parsed.getUTCHours() !== hour ||
    parsed.getUTCMinutes() !== minute || parsed.getUTCSeconds() !== second
  ) return null;
  return parsed.toISOString();
}

function oneLine(value: string, max = 240): string {
  const withoutControls = Array.from(value, (char) => {
    const code = char.codePointAt(0) ?? 0;
    return code < 0x20 || (code >= 0x7f && code <= 0x9f) || code === 0x2028 || code === 0x2029
      ? " "
      : char;
  }).join("");
  const collapsed = withoutControls.replace(/\s+/g, " ").trim();
  return Array.from(collapsed).slice(0, max).join("");
}

/** JSON string literals are valid YAML scalars and cannot introduce keys or delimiters. */
function yamlScalar(value: string): string {
  return JSON.stringify(value).replace(/\u2028/g, "\\u2028").replace(/\u2029/g, "\\u2029");
}

function digestSources(digest: DreamDigest): string[] {
  const refs = new Set<string>();
  for (const entry of digest.entries) {
    const evidence = (entry as { evidence?: unknown }).evidence;
    if (!Array.isArray(evidence)) continue;
    for (const ref of evidence) if (typeof ref === "string" && ref.length > 0) refs.add(ref);
  }
  return [...refs];
}

function renderDreamDigestArtifact(digest: DreamDigest, timestamp: string): string {
  const worker = oneLine(digest.worker) || "worker";
  const lines = [
    "---",
    "type: artifact",
    `title: ${yamlScalar(`Dream digest — ${worker}`)}`,
    `created: ${timestamp}`,
    `updated: ${timestamp}`,
    "tags:",
    "  - dream-digest",
    "origin: dream",
  ];
  const sources = digestSources(digest);
  if (sources.length > 0) {
    lines.push("source:", ...sources.map((source) => `  - ${yamlScalar(source)}`));
  }
  lines.push("---", "");
  return `${lines.join("\n")}${renderDigestMarkdown({ ...digest, worker })}`;
}

/**
 * Commit the digest as its OWN commit, AFTER the delta batch + that batch's end-state verify.
 * Keyed by the cursor-window upper bound `<cycle>` so a rebase-retry re-writes
 * the SAME pair idempotently. Append-only: never reverted on its own (the revert drill targets the
 * (delta, digest) pair). On any failure the assembled digest is returned as `reconstructible` so
 * the caller can re-emit / log it — the digest is never silently lost.
 *
 * BEST-EFFORT / NON-FATAL: this runs AFTER the deltas + cursor advance have already landed, so it
 * must NEVER reject. `fetchTip()` and `readFileAt()` THROW on non-ok HTTP / token-mint / network
 * (see writer.ts), so every leg is wrapped — a thrown error is converted to the same
 * `{ committed: false, reason: "error" }` disposition `commitBatch`'s `{ok:false}` RETURN takes,
 * so the caller's non-fatal handling covers throw and return alike.
 */
export async function commitDreamDigest(
  digest: DreamDigest,
  brain: BrainRepoWriter,
  deps: { fetchTip: () => Promise<string> },
): Promise<DigestCommitResult> {
  try {
    const timestamp = normalizeCycleTimestamp(digest.cycle);
    if (timestamp === null) {
      return { committed: false, reason: "error", reconstructible: digest };
    }
    const { json, md } = digestPaths(digest.cycle);
    const jsonContent = JSON.stringify(digest, null, 2) + "\n";
    const mdContent = renderDreamDigestArtifact(digest, timestamp);
    const expectedOldSha = await deps.fetchTip();
    const res = await brain.commitBatch({
      expectedOldSha,
      message: `dream digest: ${digest.worker} ${digest.cycle}`,
      changes: [
        { path: json, content: jsonContent },
        { path: md, content: mdContent },
      ],
    });

    if (!res.ok) {
      // non-fast-forward or error: hand the payload back, re-emittable on the next cycle.
      return { committed: false, reason: res.reason ?? "error", reconstructible: digest };
    }

    // End-state verify at the immutable commit snapshot. Reading the moving
    // branch here can race with a later digest commit and falsely report that
    // this successfully-landed payload failed verification.
    if (!res.newSha) {
      return { committed: false, reason: "verify-failed", reconstructible: digest };
    }
    const landed = await brain.readFileAt(json, res.newSha);
    if (landed === null || landed !== jsonContent) {
      return { committed: false, reason: "verify-failed", reconstructible: digest };
    }

    return { committed: true, newSha: res.newSha };
  } catch {
    // fetchTip()/readFileAt() throw on non-ok HTTP / token / network — collapse to the SAME
    // non-fatal disposition as a {ok:false} return so the digest leg is truly best-effort.
    return { committed: false, reason: "error", reconstructible: digest };
  }
}
